/**
 * AD Dangerous Privileges queries - identifying overly permissive access.
 */

import { QueryFolders } from '@/sections/insights/queryBuilder/savedQueries/folders';
import type { SavedQuery } from '@/sections/insights/queryBuilder/types/SavedQuery';

import { AD_PATHFINDING_RELATIONSHIPS } from './relationships';

export const AD_DANGEROUS_PRIVS_QUERIES: SavedQuery[] = [
  {
    id: 'dangerous-privileges-domain-users',
    name: 'Dangerous privileges for Domain Users groups',
    type: 'graph',
    description:
      'Identifies groups that have overly permissive access to domain resources.',
    folderId: QueryFolders.AD_ATTACK_PATHS,
    query: {
      node: {
        labels: ['ADGroup'],
        filters: [
          {
            field: 'objectid',
            operator: 'ENDS WITH',
            value: '-513',
            not: false,
          },
        ],
        relationships: [
          {
            label: [...AD_PATHFINDING_RELATIONSHIPS].filter(
              r => r !== 'MemberOf'
            ),
            optional: false,
            length: 1,
            target: {
              labels: ['ADObject'],
              filters: [],
              relationships: [],
            },
          },
        ],
      },
      limit: 200,
    },
  },
  {
    id: 'domain-users-laps-passwords',
    name: 'Computers where Domain Users can Read LAPS Passwords',
    type: 'graph',
    description:
      'Find computers where the Domain Users group has AllExtendedRights or ReadLAPSPassword privileges.',
    folderId: QueryFolders.AD_ATTACK_PATHS,
    query: {
      node: {
        labels: ['ADGroup'],
        filters: [
          {
            field: 'objectid',
            operator: 'ENDS WITH',
            value: '-513',
            not: false,
          },
        ],
        relationships: [
          {
            label: ['AllExtendedRights', 'ReadLAPSPassword'],
            optional: false,
            length: 1,
            target: {
              labels: ['ADComputer'],
              filters: [],
              relationships: [],
            },
          },
        ],
      },
      limit: 200,
    },
  },
  {
    id: 'domain-users-local-admins',
    name: 'Computers where Domain Users are Local Administrators',
    type: 'graph',
    description:
      'Find computers where the Domain Users group has local admin privileges.',
    folderId: QueryFolders.AD_ATTACK_PATHS,
    query: {
      node: {
        labels: ['ADGroup'],
        filters: [
          {
            field: 'objectid',
            operator: 'ENDS WITH',
            value: '-513',
            not: false,
          },
        ],
        relationships: [
          {
            label: 'AdminTo',
            optional: false,
            length: 1,
            target: {
              labels: ['ADComputer'],
              filters: [],
              relationships: [],
            },
          },
        ],
      },
      limit: 200,
    },
  },
  {
    id: 'principals-with-dcsync',
    name: 'Principals with DCSync Privileges',
    type: 'graph',
    description:
      'Find principals with DCSync, AllExtendedRights, or GenericAll privileges on the Domain. These can extract all password hashes.',
    folderId: QueryFolders.AD_ATTACK_PATHS,
    query: {
      node: {
        labels: ['ADObject'],
        filters: [],
        relationships: [
          {
            label: ['DCSync', 'AllExtendedRights', 'GenericAll'],
            optional: false,
            length: 1,
            target: {
              labels: ['ADDomain'],
              filters: [],
              relationships: [],
            },
          },
        ],
      },
      limit: 200,
    },
  },
  {
    id: 'workstations-domain-users-rdp',
    name: 'Workstations where Domain Users can RDP',
    type: 'graph',
    description:
      'Find workstations where Domain Users group has RDP access. Affects all authenticated users.',
    folderId: QueryFolders.AD_ATTACK_PATHS,
    query: {
      node: {
        labels: ['ADGroup'],
        filters: [
          {
            field: 'objectid',
            operator: 'ENDS WITH',
            value: '-513',
            not: false,
          },
        ],
        relationships: [
          {
            label: 'CanRDP',
            optional: false,
            length: 1,
            target: {
              labels: ['ADComputer'],
              filters: [
                {
                  field: 'operatingsystem',
                  operator: 'CONTAINS',
                  value: 'Server',
                  not: true,
                },
              ],
              relationships: [],
            },
          },
        ],
      },
      limit: 200,
    },
  },
  {
    id: 'servers-domain-users-rdp',
    name: 'Servers where Domain Users can RDP',
    type: 'graph',
    description:
      'Find servers where Domain Users group has RDP access. Servers are more critical than workstations.',
    folderId: QueryFolders.AD_ATTACK_PATHS,
    query: {
      node: {
        labels: ['ADGroup'],
        filters: [
          {
            field: 'objectid',
            operator: 'ENDS WITH',
            value: '-513',
            not: false,
          },
        ],
        relationships: [
          {
            label: 'CanRDP',
            optional: false,
            length: 1,
            target: {
              labels: ['ADComputer'],
              filters: [
                {
                  field: 'operatingsystem',
                  operator: 'CONTAINS',
                  value: 'Server',
                  not: false,
                },
              ],
              relationships: [],
            },
          },
        ],
      },
      limit: 200,
    },
  },
  {
    id: 'domain-admins-logons-non-dcs',
    name: 'Domain Admins Logons to Non-Domain Controllers',
    type: 'graph',
    description:
      'Find Domain Admin sessions on non-DC computers. This represents a security risk as DAs should only log on to DCs.',
    folderId: QueryFolders.AD_ATTACK_PATHS,
    query: {
      node: {
        labels: ['ADComputer'],
        filters: [{ field: 'isdc', operator: '=', value: false, not: false }],
        relationships: [
          {
            label: 'HasSession',
            optional: false,
            length: 1,
            target: {
              labels: ['ADUser'],
              filters: [],
              relationships: [
                {
                  label: 'MemberOf',
                  optional: false,
                  length: -1,
                  target: {
                    labels: ['ADGroup'],
                    filters: [
                      {
                        field: 'objectid',
                        operator: 'ENDS WITH',
                        value: '-512',
                        not: false,
                      },
                    ],
                    relationships: [],
                  },
                },
              ],
            },
          },
        ],
      },
      limit: 200,
    },
  },
];
